/**
 * A truncated table cell says what it is hiding (web/DESIGN.md §6.4).
 *
 * A cell is always one line and long values end in "…". That is only honest if
 * the whole value is one gesture away, so for every table in the app, without a
 * page threading anything through:
 *
 *   * hover -- the cell gets a `title` carrying its full text;
 *   * click -- the cell opens in place (`.cell-open`) and wraps, and a second
 *     click closes it. The table's geometry changes only because somebody asked.
 *
 * Same shape as `stack.ts`: one listener pair on <main>, installed by the shell.
 * Measuring happens on hover, never on render -- a ledger of a thousand rows
 * pays nothing until a pointer is actually over a cell.
 *
 * ⚠️ A row that is itself a link (`tr.row-link`) does NOT open on click: the
 * click belongs to the row, which navigates. Those cells keep the hover title.
 * A click on anything interactive inside a cell is left alone, as is a cell
 * that already has its own click behaviour (`td.outcome` on Books > Sync).
 */

const INTERACTIVE = "a, button, input, select, textarea, label, .menu";

function cellOf(target: EventTarget | null): HTMLTableCellElement | null {
  if (!(target instanceof Element)) return null;
  const cell = target.closest("tbody td");
  return cell instanceof HTMLTableCellElement ? cell : null;
}

/** Is the cell's content cut off? A one-pixel tolerance for sub-pixel layout. */
export function isTruncated(cell: HTMLElement): boolean {
  return cell.scrollWidth > cell.clientWidth + 1;
}

export function watchTruncation(root: Element): () => void {
  function onOver(event: Event) {
    const cell = cellOf(event.target);
    if (!cell || cell.classList.contains("cell-open")) return;
    if (isTruncated(cell)) {
      cell.classList.add("cell-trunc");
      // Never overwrite a title a page wrote on purpose.
      if (!cell.title) {
        const text = cell.textContent?.trim() ?? "";
        if (text) {
          cell.title = text;
          cell.dataset.autoTitle = "1";
        }
      }
    } else if (cell.classList.contains("cell-trunc")) {
      cell.classList.remove("cell-trunc");
      if (cell.dataset.autoTitle) {
        cell.removeAttribute("title");
        delete cell.dataset.autoTitle;
      }
    }
  }

  function onClick(event: Event) {
    const cell = cellOf(event.target);
    if (!cell) return;
    if (cell.classList.contains("outcome")) return;
    if (cell.closest("tr.row-link")) return;
    if (event.target instanceof Element && event.target.closest(INTERACTIVE)) return;
    if (cell.classList.contains("cell-open")) {
      cell.classList.remove("cell-open");
      return;
    }
    if (isTruncated(cell)) cell.classList.add("cell-open", "cell-trunc");
  }

  root.addEventListener("mouseover", onOver);
  root.addEventListener("click", onClick);
  return () => {
    root.removeEventListener("mouseover", onOver);
    root.removeEventListener("click", onClick);
  };
}
